import {
  ArrowRight,
  Calendar,
  ChevronRight,
  FileText,
  Heart,
  Phone,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import IslamicEventsWidget from "../../components/IslamicEventsWidget";
import Layout from "../../components/layout/Layout";
import PrayerTimesWidget from "../../components/PrayerTimesWidget";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { useLanguage } from "../../context/LanguageContext";
import { postsAPI } from "../../lib/api";

import backgroundImage from "../../assets/images/background.jpg";
const HomePage = () => {
  const { t, lang, isRTL } = useLanguage();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const data = await postsAPI.getAll({ status: "published", limit: 3 });
        setPosts(data);
      } catch (error) {
        console.error("Failed to fetch posts:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  const getLocalizedField = (item, field) => {
    return (
      item[`${field}_${lang}`] ||
      item[`${field}_ro`] ||
      item[`${field}_en`] ||
      ""
    );
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(
      lang === "ar" ? "ar-SA" : lang === "en" ? "en-GB" : "ro-RO",
      {
        day: "numeric",
        month: "long",
        year: "numeric",
      },
    );
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative bg-[#044F5B] text-white overflow-hidden">
        {/* Background pattern */}
        <div
          className="absolute inset-0 opacity-20 bg-cover bg-center"
          style={{ backgroundImage: `url(${backgroundImage})` }}></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Text content */}
            <div className={`${isRTL ? "lg:order-2" : ""}`}>
              <p className="text-islamic-gold font-medium mb-2">
                {t("home.welcome")}
              </p>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 leading-tight">
                {t("home.mosqueName")}
              </h1>
              <p className="text-xl text-white/80 mb-2">{t("home.subtitle")}</p>
              <p className="text-white/70 mb-8 max-w-lg leading-relaxed">
                {t("home.description")}
              </p>

              <div className="flex flex-wrap gap-4">
                <Link to="/adeverinta">
                  <Button
                    className="bg-islamic-gold text-islamic-teal hover:bg-islamic-gold/90 font-medium px-6"
                    data-testid="hero-certificate-btn">
                    <FileText className="w-4 h-4 mr-2" />
                    {t("home.getCertificate")}
                  </Button>
                </Link>
                <Link to="/dons">
                  <Button
                    variant="outline"
                    className="border-white text-white hover:bg-white/10"
                    data-testid="hero-donation-btn">
                    <Heart className="w-4 h-4 mr-2"
                  />
                    {t("home.makeDonation")}
                  </Button>
                </Link>
              </div>
            </div>

            {/* Prayer times widget */}
            <div className={`${isRTL ? "lg:order-1" : ""} space-y-6`}>
              <PrayerTimesWidget />
            </div>
          </div>
        </div>

        {/* Wave divider */}
        <div className="absolute bottom-[-1px] left-0 right-0">
          <svg
            viewBox="0 0 1440 60"
            fill="none"
            xmlns="http://www.w3.org/2000/svg">
            <path
              d="M0 60L48 55C96 50 192 40 288 35C384 30 480 30 576 33.3C672 36.7 768 43.3 864 45C960 46.7 1056 43.3 1152 40C1248 36.7 1344 33.3 1392 31.7L1440 30V60H0Z"
              fill="#F9F9F7"
            />
          </svg>
        </div>
      </section>

      {/* Islamic Events Section */}
      <section className="py-8 md:py-12 bg-islamic-pearl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-xl mx-auto">
            <IslamicEventsWidget />
          </div>
        </div>
      </section>

      {/* Quick Links Section */}
      <section className="py-12 md:py-16 bg-islamic-pearl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-serif text-2xl md:text-3xl font-semibold text-center mb-8 text-stone-800">
            {t("home.quickLinks")}
          </h2>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Certificate Card */}
            <Link to="/adeverinta" data-testid="quick-link-certificate">
              <Card className="h-full card-hover border-stone-200 bg-white group">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-lg bg-islamic-teal/10 flex items-center justify-center mb-4 group-hover:bg-islamic-teal/20 transition-colors">
                    <FileText className="w-6 h-6 text-islamic-teal" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2 text-stone-800">
                    {t("home.getCertificate")}
                  </h3>
                  <p className="text-sm text-stone-600 mb-4">
                    {t("certificate.subtitle")}
                  </p>
                  <span className="text-islamic-teal text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                    {t("common.view")}
                    <ChevronRight className="w-4 h-4" />
                  </span>
                </CardContent>
              </Card>
            </Link>

            {/* Donations Card */}
            <Link to="/dons" data-testid="quick-link-donations">
              <Card className="h-full card-hover border-stone-200 bg-white group">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-lg bg-islamic-gold/10 flex items-center justify-center mb-4 group-hover:bg-islamic-gold/20 transition-colors">
                    <Heart className="w-6 h-6 text-islamic-gold" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2 text-stone-800">
                    {t("home.makeDonation")}
                  </h3>
                  <p className="text-sm text-stone-600 mb-4">
                    {t("donations.subtitle")}
                  </p>
                  <span className="text-islamic-teal text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                    {t("common.view")}
                    <ChevronRight className="w-4 h-4" />
                  </span>
                </CardContent>
              </Card>
            </Link>

            {/* Contact Card */}
            <Link to="/contact" data-testid="quick-link-contact">
              <Card className="h-full card-hover border-stone-200 bg-white group">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-lg bg-stone-100 flex items-center justify-center mb-4 group-hover:bg-stone-200 transition-colors">
                    <Phone className="w-6 h-6 text-stone-600" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2 text-stone-800">
                    {t("home.contactUs")}
                  </h3>
                  <p className="text-sm text-stone-600 mb-4">
                    Strada Muntii Gurghiului nr 50-56 060316 BUCURESTI Romania
                  </p>
                  <span className="text-islamic-teal text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                    {t("common.view")}
                    <ChevronRight className="w-4 h-4" />
                  </span>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      </section>

      {/* Latest Announcements */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <h2 className="font-serif text-2xl md:text-3xl font-semibold text-stone-800">
              {t("home.latestNews")}
            </h2>
            <Link to="/annonces">
              <Button
                variant="ghost"
                className="text-islamic-teal hover:text-islamic-teal-light"
                data-testid="view-all-posts">
                {t("home.viewAll")}
                <ArrowRight
                  className={`w-4 h-4 ${isRTL ? "mr-2 rotate-180" : "ml-2"}`}
                />
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <Card key={i} className="border-stone-200">
                  <CardContent className="p-0">
                    <div className="h-48 bg-stone-200 animate-pulse"></div>
                    <div className="p-6">
                      <div className="h-4 bg-stone-200 rounded animate-pulse mb-3 w-1/4"></div>
                      <div className="h-6 bg-stone-200 rounded animate-pulse mb-2"></div>
                      <div className="h-4 bg-stone-200 rounded animate-pulse w-3/4"></div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : posts.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map((post) => (
                <Link
                  key={post.id}
                  to={`/annonces/${post.id}`}
                  data-testid={`post-card-${post.id}`}>
                  <Card className="h-full card-hover border-stone-200 bg-white overflow-hidden group">
                    <CardContent className="p-0">
                      {post.cover_image ? (
                        <div className="h-48 overflow-hidden">
                          <img
                            src={post.cover_image}
                            alt={getLocalizedField(post, "title")}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                      ) : (
                        <div className="h-48 bg-gradient-to-br from-islamic-teal/10 to-islamic-gold/10 flex items-center justify-center">
                          <span className="font-arabic text-4xl text-islamic-teal/30">
                            ر
                          </span>
                        </div>
                      )}
                      <div className="p-6">
                        <div className="flex items-center gap-2 text-xs text-stone-500 mb-3">
                          <Calendar className="w-3 h-3" />
                          {formatDate(post.created_at)}
                          <span className="px-2 py-0.5 bg-stone-100 rounded text-stone-600 capitalize">
                            {post.category}
                          </span>
                        </div>
                        <h3 className="font-semibold text-lg mb-2 text-stone-800 line-clamp-2 group-hover:text-islamic-teal transition-colors">
                          {getLocalizedField(post, "title")}
                        </h3>
                        <p className="text-sm text-stone-600 line-clamp-2">
                          {getLocalizedField(post, "excerpt") ||
                            getLocalizedField(post, "content").substring(
                              0,
                              150,
                            )}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          ) : (
            <Card className="border-stone-200">
              <CardContent className="p-12 text-center">
                <p className="text-stone-500">{t("common.noResults")}</p>
              </CardContent>
            </Card>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default HomePage;
